import { makeAutoObservable } from 'mobx'
import { memberMobx, multiPersonMobx, payPlanMobx, storeMobx } from '@mobxStore'
import { validMobileOrMail } from '@dt/fe-utils'
import { handleEleScrollTo, localStore } from '@utils'
import { cartMobx, restaurantConfig, restaurantCenter, RESTAURANT_EVENT } from 'restaurant'
import { isEmpty } from '@dt/fe-utils/dist/lodash'

type TGuest = 'tourist' | 'oldGuest'

interface IContactInfo {
  contactName: string;
  contactMobile: string;
  countryCode: string;
}

interface IRemarkInfo {
  list?: string[];
  custom?: string;
}

class Checkout {
  constructor () {
    makeAutoObservable(this, {
      // setContactInfo: action
    }, {
      autoBind: true
    })
    // 如果重置购物车时则重置备注信息
    restaurantCenter.on(RESTAURANT_EVENT.CART_RESET, () => {
      this.resetRemarkInfo()
    })
  }
  /** 游客昵称 */
  touristNickName: string = localStore.get('touristNickName') || ''
  /** 是否保存订单信息 */
  isSaveOrderInfo: boolean = true
  /** 订单模式：游客/老顾客 */
  guestType: TGuest = (memberMobx.isLogin && memberMobx.memberInfo.type !== 'member') || !memberMobx.isLogin ? 'tourist' : 'oldGuest'
  /** 是否显示详情 */
  showDetail: boolean = false
  /** 是否提示错误（游客昵称没填，会员没有登录） */
  showCheckoutMemberError: boolean = false
  /** 不再展示手机号空提示 */
  neverShowMobileTips: boolean = false
  // 是否展示引导用户登录弹框
  guideRegister: boolean = false

  contactInfo: IContactInfo = localStore.get(
    `contactInfo.${Config.BRANDID}`
  ) || {
    contactName: '',
    contactMobile: memberMobx.isLogin ? memberMobx.memberInfo?.mobile : '',
    countryCode: memberMobx.isLogin ? memberMobx.memberInfo?.countryCode : (window.bo === 'cn' ? 86 : window.bo === 'us' ? 1 : 49)
  }

  contactError: any = { nameError: false, mobileError: false, addressError: false }

  remarkInfo: IRemarkInfo = { list: [], custom: '' }

  /** 返回当前应该显示在购物车中的备注信息 (已下单不再显示整单备注) */
  get cartRemarkInfo (): string {
    if (cartMobx.info.remark && !cartMobx.ordersId) {
      return cartMobx.info.remark
    }
    const remarkInfos = cartMobx.cartItems.find((cartItem) => !!cartItem.remark)
    return remarkInfos ? remarkInfos.remark : ''
  }

  /** 获取取餐人称呼 */
  get contactName () {
    if (memberMobx.isLogin && memberMobx.memberInfo.type === 'member') {
      return memberMobx.memberInfo.nickName
    } else {
      return multiPersonMobx.currentPersonInfo?.nickName || this.touristNickName
    }
  }

  /** 获取当前是否登录了会员 */
  get isMemberLogin () {
    return memberMobx.isLogin && memberMobx.memberInfo.type === 'member'
  }

  /** 获取联系人信息 */
  get checkoutContactInfos () {
    let memberContact = {} as IContactInfo
    if (memberMobx.isLogin) {
      memberContact = {
        contactName: memberMobx.memberInfo.nickName,
        contactMobile: memberMobx.memberInfo.mobile,
        countryCode: String(memberMobx.memberInfo.countryCode),
      }
    }
    if (Config.codeType === 'table') {
      return {
        ...memberContact,
        contactName: multiPersonMobx.currentPersonInfo?.nickName || memberContact?.contactName,
      }
    } else {
      return {
        contactName: this.contactInfo?.contactName || memberContact?.contactName,
        contactMobile: this.contactInfo?.contactMobile || memberContact?.contactMobile,
        countryCode: String(this.contactInfo?.countryCode || memberContact?.countryCode),
      }
    }
  }

  /** 必填手机号 */
  get mustMobileInput () {
    const { requiredPhoneConfig } = storeMobx.info
    const payMode = storeMobx.storeSceneConfig.payMode
    if (['table', 'store'].includes(restaurantConfig.codeType)) {
      if (requiredPhoneConfig?.scanPayFirst && !payMode || requiredPhoneConfig?.scanPayLast && payMode) {
        return true
      }
    } else {
      if (cartMobx.info?.eatType === 'delivery') {
        return false
      } else if (requiredPhoneConfig?.mobilePayFirst && !payMode || requiredPhoneConfig?.mobilePayLast && payMode) {
        return true
      }
    }
    return false
  }

  /** 堂食能打包 */
  get dineinCanTogo () {
    return cartMobx.info.eatType === 'dinein' && !storeMobx.info.closeDineinTogo
  }

  /** 联系信息是否填写 */
  isContactInfoFill (noScroll?: boolean, type: 'mall' | '' = '') {
    if (type === 'mall') {
      this.contactError = {
        nameError: !this.contactInfo.contactName,
        mobileError: !validMobileOrMail('mobile', this.contactInfo.contactMobile, this.contactInfo.countryCode),
        addressError: !cartMobx.info.contactName || !cartMobx.info.contactMobile || isEmpty(cartMobx.info?.contactInfo)
      }
    } else {
      const { requiredPhoneConfig, payMode } = storeMobx.info
      // 桌码无需填写信息
      // if (restaurantConfig.codeType === 'table') {
      //   return true
      // }
      // 扫码点餐
      if (['table', 'store'].includes(restaurantConfig.codeType)) {
        if (this.mustMobileInput) {
          this.contactError = {
            nameError: window.bo !== 'cn' && !this.contactInfo.contactName,
            mobileError: !validMobileOrMail('mobile', this.contactInfo.contactMobile, this.contactInfo.countryCode)
          }
        }
      } else {
        if (cartMobx.info.eatType === 'delivery') {
          this.contactError = {
            addressError: !cartMobx.info.contactName || !cartMobx.info.contactMobile || isEmpty(cartMobx.info?.contactInfo)
          }
        } else if (this.mustMobileInput) {
          this.contactError = {
            nameError: window.bo !== 'cn' && !this.contactInfo.contactName,
            mobileError: !validMobileOrMail('mobile', this.contactInfo.contactMobile, this.contactInfo.countryCode)
          }
        }
      }
    }

    if (Object.values(this.contactError).every(item => !item)) {
      return true
    }

    if (!noScroll) {
      const ele: HTMLElement = document.querySelector(
        cartMobx.info.eatType === 'delivery' ? '.checkout-address-wrapper' : '.checkout-contact-container'
      )
      ele &&
        handleEleScrollTo({
          selector: 'ion-content.checkout-content',
          y: ele.offsetTop || 0,
        })
    }

    return false
  }

  setTouristNickName (name: string) {
    localStore.set('touristNickName', name || '')
    this.touristNickName = name || ''
  }

  setIsSaveOrderInfo (isSave: boolean) {
    this.isSaveOrderInfo = isSave
  }

  setGuestType (val: TGuest) {
    this.guestType = val
    this.setShowCheckoutMemberError(false)
  }

  /** 显示未登录或未填昵称的错误 */
  setShowCheckoutMemberError (val: boolean) {
    this.showCheckoutMemberError = val
    if (val) {
      const ele: HTMLElement = document.querySelector(
        '.checkout-member-login-container'
      )
      ele &&
        handleEleScrollTo({
          selector: 'ion-content.checkout-content',
          y: ele.offsetTop || 0,
        })
    }
  }

  setShowDetail (isShowDetail?: boolean) {
    if (this.showDetail !== isShowDetail) {
      this.showDetail =
        typeof isShowDetail === 'undefined' ? !this.showDetail : isShowDetail
    }
  }

  /** 设置取餐人信息 */
  setContactInfo (info: IContactInfo) {
    this.contactInfo = {
      ...info,
    }
    this.contactError = {
      nameError: false,
      mobileError: false,
      addressError: false,
    }
    localStore.set(`contactInfo.${Config.BRANDID}`, info)
  }

  /** 重置用户信息录入错误提示 */
  resetContactError () {
    this.contactError = {
      nameError: false,
      mobileError: false,
    }
  }

  /** 不再展示未填手机号提示 */
  setNeverShowMobileTips () {
    this.neverShowMobileTips = true
  }

  /** 设置备注信息 */
  setRemarkInfo (info: IRemarkInfo) {
    const { list = [], custom = '' } = info
    this.remarkInfo = { list, custom }
    const remark = [...list, custom].filter(item => !!item).join(bo === 'cn' ? '，' : ',')
    cartMobx.setRemark(remark || '')
  }

  /** 获取备注信息 */
  getRemarkInfo () {
    const { list = [], custom = '' } = this.remarkInfo
    this.remarkInfo = { list, custom }
    const remark = [...list, custom].filter(item => !!item).join(bo === 'cn' ? '，' : ',')
    return remark
  }

  /** 重置备注信息 */
  resetRemarkInfo () {
    this.remarkInfo = { list: [], custom: '' }
  }

  /** 堂食不支持打包重置 */
  resetDineinTogos () {
    if (cartMobx.info.eatType === 'dinein' && storeMobx.info.closeDineinTogo) {
      cartMobx.packAll(0)
    }
  }

  /** 设置是否需要引导用户登录 */
  setGuideRegister (val?: boolean) {
    this.guideRegister = !!val
  }
}

if (window.MX) {
  window.MX.memberMobx = memberMobx
}

export default new Checkout()
